import { Button, Menu, Select, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LetterTemplateCreate } from "../interfaces/LetterTemplate";
import {
	CreateLetterTemplate,
	GetLetterTemplate,
	UpdateLetterTemplate,
} from "../requester/functions";
import VO, { getUrlId, VK } from "../utilities/validate";
import EmailEditor, { EditorRef } from "react-email-editor";
import DataTextField from "../components/DataInputs/DataTextField";
import DataSelect from "../components/DataInputs/DataSelect";

interface IProps {}

const templateTemp: LetterTemplateCreate = {
	name: "",
	header: "",
};

const copyVars = [
	{ key: "candidate", name: "Имя кандидата", value: "{{ name_candidate }}" },
	{ key: "vacancy", name: "Название вакансии", value: "{{ name_vacancy }}" },
];

const LetterTemplatePage = (props: IProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();

	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [firstLoad, setFirstLoad] = useState<boolean>(true);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const [template, setTemplate] = useState<LetterTemplateCreate>(templateTemp);

	const emailEditorRef = useRef<EditorRef>(null);

	const onSave = () => {
		emailEditorRef.current?.editor?.exportHtml((data) => {
			const { design, html } = data;
			if (!isEdit)
				CreateLetterTemplate(
					{
						...template,
						html: html,
						json_data: JSON.stringify(design),
					},
					(resp) => {
						if (resp.status === "ok") {
							enqueueSnackbar("Шаблон успешно создан", {
								variant: "success",
							});
							setIsEdit(true);
							navigate(`/templates`);
						} else {
							enqueueSnackbar(`Ошибка ${resp.code}, ${resp.message}`, {
								variant: "error",
							});
						}
					}
				);
			else
				UpdateLetterTemplate(
					{
						...template,
						html: html,
						json_data: JSON.stringify(design),
					},
					(resp) => {
						if (resp.status === "ok") {
							enqueueSnackbar("Шаблон успешно обновлен", {
								variant: "success",
							});
						} else {
							enqueueSnackbar(`Ошибка ${resp.code}, ${resp.message}`, {
								variant: "error",
							});
						}
					}
				);
		});
	};

	const onReady = () => {
		if (isEdit && firstLoad && template.json_data) {
			emailEditorRef.current?.editor?.loadDesign(
				JSON.parse(template.json_data || "{}")
			);
			setFirstLoad(false);
		}
	};

	const onCopy = (value: string) => {
		navigator.clipboard.writeText(value);
		enqueueSnackbar("Текст скопирован", { variant: "success" });
	};

	useEffect(() => {
		const id: string = getUrlId(location.pathname);
		if (id === "create") setIsLoaded(true);
		else
			GetLetterTemplate({ id: parseInt(id) }, (resp) => {
				if (resp.status === "ok" && resp.data) {
					setTemplate(resp.data);
					setIsLoaded(true);
					setIsEdit(true);
				} else {
					enqueueSnackbar("Ошибка при получении шаблона", {
						variant: "error",
					});
				}
			});
	}, []);

	return isLoaded ? (
		<div className="w-full flex flex-col gap-8">
			<div className="flex gap-4">
				<DataTextField
					value={template}
					setValue={setTemplate}
					keyValue={"name"}
					inputProps={{
						required: true,
						label: "Название",
						error: !VK(template.name),
					}}
				/>
				<DataTextField
					value={template}
					className="w-full"
					setValue={setTemplate}
					keyValue={"header"}
					inputProps={{
						required: true,
						label: "Заголовок",
						error: !VK(template.header),
					}}
				/>
				<Button
					variant="contained"
					onClick={(event) => {
						setAnchorEl(event.currentTarget);
					}}
				>
					Вставка
				</Button>

				<Button
					variant="contained"
					disabled={!VO(template, templateTemp)}
					onClick={onSave}
				>
					Cохранить
				</Button>
			</div>
			<EmailEditor
				ref={emailEditorRef}
				minHeight="70vh"
				appearance={{ theme: theme.palette.mode }}
				onReady={onReady}
				locale="ru-RU"
			/>
			<Menu
				open={anchorEl ? true : false}
				anchorEl={anchorEl}
				onClose={() => {
					setAnchorEl(null);
				}}
				variant={"menu"}
				className="overflow-visible"
				sx={{ overflowX: "unset", overflowY: "unset" }}
			>
				<div className="flex flex-col gap-3 px-3 py-4">
					{copyVars.map((opt) => (
						<div
							key={`opt-${opt.key}`}
							className="cursor-pointer hover:"
							onClick={() => {
								onCopy(opt.value);
								setAnchorEl(null);
							}}
						>
							{opt.name}
						</div>
					))}
				</div>
			</Menu>
		</div>
	) : null;
};

export default LetterTemplatePage;
